import {
  CircleF,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api';
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { googleMapId, googleMapsApiKey } from '../../../../../services/api';
import {
  GeolocationLocationSet,
  GeolocationLocationSetData,
} from '../../../../../types';
import { DEFAULT_LAT, DEFAULT_LNG } from '../../../../../utils';
import { buildAddress } from '../../../../../utils/helpers/DiyHelpers';
import { Box } from '@mui/material';
import { humanizeString } from '../../../../../utils/stringModifier';

const containerStyle = {
  width: '100%',
  height: '400px',
};

const DEFAULT_CIRCLE_STYLE = {
  strokeColor: '#0000FF',
  strokeOpacity: 0.8,
  strokeWeight: 1,
  fillColor: '#0000FF',
  fillOpacity: 0.1,
  clickable: false,
  draggable: false,
  editable: false,
  visible: true,
  zIndex: 1,
};

interface FacebookMapProps {
  setParentMap?: Dispatch<SetStateAction<any>>;
  geolocations?: GeolocationLocationSet;
  excludedGeolocations?: GeolocationLocationSet;
  search?: boolean;
  searchResult?: any;
  setSearchResult?: Dispatch<SetStateAction<any>>;
}

const FacebookMap: React.FC<FacebookMapProps> = ({
  setParentMap,
  geolocations,
  excludedGeolocations,
  search = false,
  searchResult,
  setSearchResult,
}) => {
  const [map, setMap] = useState<any>(null);
  const [center, setCenter] = useState<any>({ lat: 0, lng: 0 });
  const [markers, setMarkers] = useState<any[]>([]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey,
    libraries: ['places', 'maps'],
    mapIds: [googleMapId],
  });

  const onLoad = useCallback((map) => {
    setMap(map);

    if (setParentMap) setParentMap(map);
  }, []);

  const onUnmount = React.useCallback(() => {
    setMap(null);

    if (setParentMap) setParentMap(null);
  }, []);

  useEffect(() => {
    if (search && searchResult?.latitude && searchResult?.longitude) {
      setCenter({
        lat: searchResult?.latitude || DEFAULT_LAT,
        lng: searchResult?.longitude || DEFAULT_LAT,
      });
      let temp = [
        {
          lat: searchResult?.latitude,
          lng: searchResult?.longitude,
          radius: searchResult?.radius || 50,
        },
      ];

      setMarkers(temp);
    } else {
      setCenter({
        lat: DEFAULT_LAT,
        lng: DEFAULT_LNG,
      });
    }
  }, [search, searchResult]);

  useEffect(() => {
    // https://developers.google.com/maps/documentation/javascript/dds-boundaries/style-polygon#overview
    if (map && !search) {
      /**
       * Location Boundary Line Style
       * @description Draw a line for the location boundaries. Included location boundary lines are in blue
       *              and excluded location are in red
       * @author Angelo David <angelod@codev.com>
       * @param excluded
       * @returns <Object> Style object for the feature style options
       */
      const boundaryOptions = (excluded: boolean) => {
        const data: google.maps.FeatureStyleOptions = {
          strokeColor: excluded ? '#FF0000' : '#810FCB',
          strokeOpacity: 1.0,
          strokeWeight: 3.0,
          fillOpacity: 0.5,
        };

        return data;
      };

      const { cities, regions, zips, countries, electoral_districts } =
        geolocations ?? {};

      const {
        cities: excludedCities,
        regions: excludedRegions,
        zips: excludedZips,
        electoral_districts: excludedElectoralDistricts,
        countries: excludedCountries,
      } = excludedGeolocations ?? {};

      // https://developers.google.com/maps/documentation/javascript/reference/data-driven-styling#FeatureType
      let countryLayer: any = map.getFeatureLayer(
        google.maps.FeatureType.COUNTRY,
      );

      let stateLayer: any = map.getFeatureLayer(
        google.maps.FeatureType.ADMINISTRATIVE_AREA_LEVEL_1,
      );

      let zipLayer: any = map.getFeatureLayer(
        google.maps.FeatureType.POSTAL_CODE,
      );

      let countyLayer: any = map.getFeatureLayer(
        google.maps.FeatureType.ADMINISTRATIVE_AREA_LEVEL_2,
      );

      let locality: any = map.getFeatureLayer(google.maps.FeatureType.LOCALITY);

      const placeIds = [
        ...(cities || []),
        ...(regions || []),
        ...(zips || []),
        ...(countries || []),
        ...(electoral_districts || []),
      ].map((location: any) => {
        return location.placeId;
      });

      const excludedPlaceIds = [
        ...(excludedCities || []),
        ...(excludedRegions || []),
        ...(excludedZips || []),
        ...(excludedElectoralDistricts || []),
        ...(excludedCountries || []),
      ].map((location: any) => {
        return location.placeId;
      });

      countryLayer.style = (options: { feature: { placeId: string } }) => {
        if (
          [...placeIds, ...excludedPlaceIds].includes(options.feature.placeId)
        ) {
          const excluded = excludedPlaceIds.includes(options.feature.placeId);

          return boundaryOptions(excluded);
        }
      };

      stateLayer.style = (options: { feature: { placeId: string } }) => {
        if (
          [...placeIds, ...excludedPlaceIds].includes(options.feature.placeId)
        ) {
          const excluded = excludedPlaceIds.includes(options.feature.placeId);

          return boundaryOptions(excluded);
        }
      };

      zipLayer.style = (options: { feature: { placeId: string } }) => {
        if (
          [...placeIds, ...excludedPlaceIds].includes(options.feature.placeId)
        ) {
          const excluded = excludedPlaceIds.includes(options.feature.placeId);

          return boundaryOptions(excluded);
        }
      };

      countyLayer.style = (options: { feature: { placeId: string } }) => {
        if (
          [...placeIds, ...excludedPlaceIds].includes(options.feature.placeId)
        ) {
          const excluded = excludedPlaceIds.includes(options.feature.placeId);

          return boundaryOptions(excluded);
        }
      };

      locality.style = (options: { feature: { placeId: string } }) => {
        if (
          [...placeIds, ...excludedPlaceIds].includes(options.feature.placeId)
        ) {
          const excluded = excludedPlaceIds.includes(options.feature.placeId);

          return boundaryOptions(excluded);
        }
      };
    }
  }, [map, geolocations, excludedGeolocations, search]);

  useEffect(() => {
    if (isLoaded && !search) {
      let temp: any[] = [];
      /*
        const { latitude, longitude } =
          (template?.adSetTemplate?.targeting?.geo_locations?.custom_locations ||
            [])[0] || {};
            */

      const {
        custom_locations,
        cities,
        regions,
        zips,
        countries,
        electoral_districts,
      } = geolocations ?? {};

      [
        ...(custom_locations || []),
        ...(cities || []),
        ...(regions || []),
        ...(zips || []),
        ...(countries || []),
        ...(electoral_districts || []),
      ].forEach((location: GeolocationLocationSetData) => {
        const { latitude, longitude, radius } = location ?? {};

        if (!latitude || !longitude) return;

        const address = !location.type
          ? humanizeString(location.address_string)
          : buildAddress(location);

        if (location.type === 'city' || !location.type) {
          temp = [
            ...temp,
            {
              lat: latitude,
              lng: longitude,
              radius,
              address,
              type: location.type,
            },
          ];
        } else {
          temp = [
            ...temp,
            { lat: latitude, lng: longitude, address, type: location.type },
          ];
        }
      });

      setMarkers(temp);
    }
  }, [geolocations, excludedGeolocations, isLoaded, search]);

  useEffect(() => {
    if (isLoaded && !search) {
      extendZoom();
    }
  }, [markers, isLoaded, search]);

  const extendZoom = () => {
    const bounds = new window.google.maps.LatLngBounds();
    if (markers.length > 0) {
      markers.forEach((item: any) => {
        bounds.extend({ lat: item.lat, lng: item.lng });
      });

      (map as any)?.fitBounds(bounds);

      if (markers.length === 1) {
        (map as any)?.setZoom(buildZoom(markers[0]));
      }
    }
  };

  const buildZoom = (marker: any) => {
    let zoom: number = 4;

    switch (marker.type) {
      case 'region':
        zoom = 6;
        break;
      case 'zip':
        zoom = 12;
        break;
      case 'city':
        zoom = 8;
        break;
      case 'country':
        zoom = 3;
        break;
      default:
        zoom = 10;
        break;
    }

    return zoom;
  };

  const circleOptions = (marker: any) => {
    let circleOptions: google.maps.CircleOptions = {
      ...DEFAULT_CIRCLE_STYLE,
      center: {
        lat: marker.lat,
        lng: marker.lng,
      },
      radius: marker.radius * 1609.34,
    };

    return circleOptions;
  };

  const handleOnClickMap = (e: any) => {
    setSearchResult({
      ...searchResult,
      latitude: e.latLng.lat(),
      longitude: e.latLng.lng(),
    });
  };

  return isLoaded ? (
    <GoogleMap
      id="search-box-example"
      mapContainerStyle={containerStyle}
      center={center ? center : { lat: DEFAULT_LAT, lng: DEFAULT_LNG }}
      zoom={!search ? 4 : 14}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={search ? handleOnClickMap : null}
      options={{
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        //zoomControl: !isAudience,
        zoomControl: true,
        mapId: googleMapId,
      }}
    >
      <Fragment>
        {markers.length > 0
          ? markers?.map((marker: any, index: number) => {
              return (
                <Box key={`map-component-${index + 1}`}>
                  <Marker
                    position={{ lat: marker.lat, lng: marker.lng }}
                    title={marker.address}
                    key={`marker-${index + 1}`}
                  />

                  {marker.radius ? (
                    <CircleF
                      options={circleOptions(marker)}
                      key={`radius-${index + 1}`}
                    />
                  ) : null}
                </Box>
              );
            })
          : null}
      </Fragment>
    </GoogleMap>
  ) : null;
};

export default FacebookMap;
